import { List, useModalState } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { ThemeProvider } from "@emotion/react";
import { IonAlert } from "@ionic/react";
import { Stack } from "@mui/material";
// eslint-disable-next-line no-restricted-imports
import { useDefinedEnv } from "@src/app/store/helperHooks/sessionHelpers";
// eslint-disable-next-line no-restricted-imports
import { logout } from "@src/app/store/session/actions";
import { AppVersionInfo } from "@src/appV2/Accounts/AppVersionInfo/AppVersionInfo";
import { LogoutConfirmationBottomSheet } from "@src/appV2/Accounts/Logout/LogoutConfirmationBottomSheet";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { AccountPageBannersContainer } from "@src/appV2/redesign/Accounts/Banners/AccountPageBannersContainer";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { USER_EVENTS } from "@src/constants";
import { useEffect, useRef, useState } from "react";
// eslint-disable-next-line no-restricted-imports
import { useDispatch } from "react-redux";

import { PageWrapper } from "../components/PageWrapper";
import { StickyScrollPageHeader } from "../components/StickyScrollPageHeader";
import { Navbar } from "../Navbar";
import { useSetNotificationPreferences } from "../NotificationPreferences/api/useSetNotificationPreferences";
import { PageContent } from "../WorkerShifts/components/PageContent";
import { AccountListItem } from "./components/AccountListItem";
import { GetHelpDuringShiftBottomSheet } from "./components/GetHelpDuringShiftBottomSheet";
import { AccountsPageDocumentsSection } from "./Sections/DocumentsSection";
import { AccountsPageJobsProfileSection } from "./Sections/JobsProfileSection";
import { AccountsPageLegalSection } from "./Sections/LegalSection";
import { AccountsPageSettingsSection } from "./Sections/SettingsSection";
import { AccountsPageSupportSection } from "./Sections/SupportSection";
import { AccountsPageTestingSection } from "./Sections/TestingSection";
import { useSetNotificationPopUpShown } from "./Sections/useSetNotificationPopUpShown";
import { logAccountPageMenuItemTapped } from "./utils";

export function AccountsPage() {
  const dispatch = useDispatch();
  const logoutModal = useModalState();
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);
  const [isClosed, setIsClosed] = useState<boolean>(false);
  const [pnChecked, setPNChecked] = useState(false);
  const [pushFlag, setPushFlag] = useState<boolean>(false);
  const [isNotificationAlertShown, setIsNotificationAlertShown] = useState<boolean>(true);
  const { mutateAsync: setNotificationPreference } = useSetNotificationPreferences();
  const worker = useDefinedWorker();
  const { userId } = worker;
  const environment = useDefinedEnv();

  const { mutateAsync: setNotificationPopUpShown } = useSetNotificationPopUpShown();

  useEffect(() => {
    if (!environment) {
      return;
    }

    setPNChecked(Boolean(environment?.notificationPreferences?.push));
    setPushFlag(Boolean(environment?.PushNotificationOnAccountTab));
    setIsClosed(true);
  }, [environment]);

  useEffect(() => {
    setIsNotificationAlertShown(Boolean(worker.isNotificationAlertShown));
  }, [worker]);

  useLogEffect(USER_EVENTS.VIEWED_ACCOUNT);

  const getHelpDuringShiftModalState = useModalState();

  const dismissAlert = async () => {
    try {
      setIsClosed(false);
      await setNotificationPopUpShown();
      await setNotificationPreference({
        notificationPreference: { key: "push", value: true },
      });
    } catch (error) {
      logError(APP_V2_APP_EVENTS.SET_NOTIFICATION_PREFERENCES_FAILURE, { error });
    }
  };

  const scrollRef = useRef<HTMLDivElement>(null);

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper variant="tertiary">
        <StickyScrollPageHeader
          hideBackButton
          title="Account"
          scrollContainerRef={scrollRef}
          headerVariant="tertiary"
        />

        <PageContent>
          <AccountPageBannersContainer workerId={userId} />

          <List
            ref={scrollRef}
            component={Stack}
            sx={{
              "& li": {
                height: 62,
              },
            }}
          >
            <AccountsPageTestingSection />
            <AccountsPageDocumentsSection />
            <AccountsPageJobsProfileSection />
            <AccountsPageSettingsSection />
            <AccountsPageSupportSection
              onGetHelpClick={() => {
                getHelpDuringShiftModalState.openModal();
              }}
              onContactSupportClick={() => {
                getHelpDuringShiftModalState.openModal();
              }}
            />
            <AccountsPageLegalSection />

            <AccountListItem
              primaryText="Sign out"
              onClick={() => {
                logAccountPageMenuItemTapped("Logout");
                logEvent(USER_EVENTS.TAPPED_LOGOUT);
                logoutModal.openModal();
              }}
            />
          </List>

          <Stack alignItems="center" marginTop={5}>
            <AppVersionInfo />
          </Stack>
        </PageContent>

        <Navbar />

        <LogoutConfirmationBottomSheet
          modalState={logoutModal}
          isLoading={isLoggingOut}
          onLogout={async () => {
            setIsLoggingOut(true);
            dispatch(logout());
          }}
        />

        <GetHelpDuringShiftBottomSheet modalState={getHelpDuringShiftModalState} />

        <IonAlert
          isOpen={!pushFlag && !isNotificationAlertShown && !pnChecked && isClosed}
          header="Notifications"
          message="For this app to work, we'll need push notifications to notify you of your upcoming shifts, open shifts and raffles."
          backdropDismiss={false}
          buttons={["Ok"]}
          onDidDismiss={dismissAlert}
        />
      </PageWrapper>
    </ThemeProvider>
  );
}
